import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/store';
import { fetchWeatherData, setSelectedLocation, fetchWeatherLocations } from '../../features/weather/weatherSlice';
import WeatherDisplay from './WeatherDisplay';
import WeatherForecast from './WeatherForecast';
import WeatherLocationForm from './WeatherLocationForm';
import Card from '../ui/Card';
import Button from '../ui/Button';
import ErrorMessage from '../ui/ErrorMessage';
import { MapPin, Plus, RefreshCw } from 'react-feather';

const WeatherWidget: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { selectedLocation, locations, error, loading } = useSelector((state: RootState) => state.weather);
  const [showAddLocation, setShowAddLocation] = useState(!locations.length);

  useEffect(() => {
    // Fetch locations when component mounts
    dispatch(fetchWeatherLocations());
  }, [dispatch]);

  useEffect(() => {
    if (selectedLocation) {
      const location = locations.find(loc => loc.id === selectedLocation);
      if (location) {
        dispatch(fetchWeatherData(location.name));
      }
    } else if (locations.length > 0) {
      // Auto-select first location if none is selected
      dispatch(setSelectedLocation(locations[0].id));
    }
  }, [dispatch, selectedLocation, locations]);

  const handleLocationChange = (locationId: string) => {
    dispatch(setSelectedLocation(locationId));
  };

  const handleRefresh = () => {
    if (selectedLocation) {
      const location = locations.find(loc => loc.id === selectedLocation);
      if (location) {
        dispatch(fetchWeatherData(location.name));
      }
    }
  };

  return (
    <div className="space-y-4">
      <Card className="bg-gradient-to-br from-blue-50 to-green-50 shadow-md hover:shadow-lg transition-all duration-300">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              {t('weather.title')}
            </h2>
            <div className="flex items-center space-x-2">
              {selectedLocation && !showAddLocation && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleRefresh}
                  className="flex items-center gap-1 py-1 px-2.5 text-sm"
                >
                  <RefreshCw size={14} />
                  <span className="hidden sm:inline">{t('common.refresh')}</span>
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowAddLocation(!showAddLocation)}
                className="flex items-center gap-1 py-1 px-2.5 text-sm"
              >
                {showAddLocation ? (
                  t('common.cancel')
                ) : (
                  <>
                    <Plus size={14} />
                    <span className="hidden sm:inline">{t('weather.addLocation')}</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {error && <ErrorMessage message={error} />}

          {showAddLocation ? (
            <WeatherLocationForm onSuccess={() => setShowAddLocation(false)} />
          ) : (
            <>
              {locations.length > 0 ? (
                <div className="space-y-4">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <MapPin className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      value={selectedLocation || ''}
                      onChange={(e) => handleLocationChange(e.target.value)}
                      className="block w-full pl-10 pr-3 py-3 rounded-lg border border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm bg-white"
                    >
                      <option value="">{t('weather.selectLocation')}</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <WeatherDisplay />
                </div>
              ) : (
                <div className="text-center py-8 bg-white bg-opacity-50 rounded-lg">
                  <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">
                    {t('weather.noLocations')}
                  </p>
                  <Button 
                    onClick={() => setShowAddLocation(true)}
                    size="sm"
                  >
                    {t('weather.addLocation')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Card>
      <WeatherForecast />
    </div>
  );
};

export default WeatherWidget;